import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUnitStore } from '../store/unitStore';
import { useAuthStore } from '../store/authStore';
import { useRealtimeStore } from '../store/realtimeStore';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import UnitFilter from '../components/UnitFilter';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// An SOS still demanding attention (not yet resolved/cancelled).
const ACTIVE_SOS_STATUSES = ['TRIGGERED', 'ACKNOWLEDGED', 'RESPONDING'];

export default function UnitStatisticsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { units, getEffectiveUnitIds, isHQ } = useUnitStore();
  const { locations, sosEvents, alerts } = useRealtimeStore();

  const userIsHQ = isHQ(user?.unitId ?? null);

  // Unit Analysis is an HQ-only comparison surface. Only redirect once units
  // are loaded — before that isHQ() is a false negative on hard navigation.
  useEffect(() => {
    if (units.length > 0 && !userIsHQ) navigate('/');
  }, [units.length, userIsHQ, navigate]);

  const effectiveUnitIds = getEffectiveUnitIds(user?.unitId ?? null);

  // Compare the non-HQ units; HQ has no area of its own to analyse.
  const analysisUnits = useMemo(
    () => units.filter((u) => u.role !== 'HQ'),
    [units]
  );

  // Live activity per unit, grouped by unitId only. This is intentionally
  // forward-compatible with the upcoming platoon reshape: no personnel PII,
  // no service numbers, no role-of-person classification.
  const unitStats = useMemo(() => {
    const activeSos = sosEvents.filter(
      (e) =>
        ACTIVE_SOS_STATUSES.includes(e.status) &&
        (!effectiveUnitIds || effectiveUnitIds.includes(e.unitId || ''))
    );
    const activeAlerts = alerts.filter(
      (a) =>
        a.status === 'ACTIVE' &&
        (!effectiveUnitIds || effectiveUnitIds.includes(a.unitId || ''))
    );

    const byUnit: Record<string, { sos: number; alerts: number }> = {};
    analysisUnits.forEach((unit) => {
      byUnit[unit.code] = {
        sos: activeSos.filter((e) => e.unitId === unit.id).length,
        alerts: activeAlerts.filter((a) => a.unitId === unit.id).length,
      };
    });
    return byUnit;
  }, [sosEvents, alerts, analysisUnits, effectiveUnitIds]);

  const totals = useMemo(() => {
    const values = Object.values(unitStats);
    return {
      sos: values.reduce((sum, s) => sum + s.sos, 0),
      alerts: values.reduce((sum, s) => sum + s.alerts, 0),
    };
  }, [unitStats]);

  // Live-tracked devices is a brigade-wide realtime count. Locations carry no
  // top-level unitId, so it is reported as a total rather than split per unit.
  const liveTracked = locations.size;

  const comparisonData = {
    labels: analysisUnits.map((u) => u.code),
    datasets: [
      {
        label: 'Active SOS',
        data: analysisUnits.map((u) => unitStats[u.code]?.sos || 0),
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        borderColor: 'rgba(239, 68, 68, 1)',
        borderWidth: 2,
      },
      {
        label: 'Active Alerts',
        data: analysisUnits.map((u) => unitStats[u.code]?.alerts || 0),
        backgroundColor: 'rgba(245, 158, 11, 0.8)',
        borderColor: 'rgba(245, 158, 11, 1)',
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: { color: '#e5e7eb', font: { size: 9 }, boxWidth: 10, padding: 6 },
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        titleColor: '#f3f4f6',
        bodyColor: '#f3f4f6',
        borderColor: '#374151',
        borderWidth: 1,
        titleFont: { size: 10 },
        bodyFont: { size: 9 },
        padding: 8,
      },
    },
    scales: {
      x: {
        ticks: { color: '#9ca3af', font: { size: 9 } },
        grid: { color: '#374151', display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { color: '#9ca3af', font: { size: 9 }, precision: 0 },
        grid: { color: '#374151' },
      },
    },
  };

  if (!userIsHQ) return null;

  return (
    <div className="min-h-screen bg-command-bg p-2 space-y-2">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg font-display font-bold text-gray-100">
            📊 Unit Analysis
          </h1>
          <p className="text-xs text-gray-400">
            Live unit-by-unit activity comparison — HQ only
          </p>
        </div>
        <UnitFilter />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="p-2 rounded-lg border border-command-border bg-command-panel">
          <div className="text-[10px] text-gray-400 mb-0.5">Units Compared</div>
          <div className="text-xl font-bold text-cyan-400">{analysisUnits.length}</div>
        </div>
        <div className="p-2 rounded-lg border border-command-border bg-command-panel">
          <div className="text-[10px] text-gray-400 mb-0.5">Live Tracked (Brigade)</div>
          <div className="text-xl font-bold text-green-400">{liveTracked}</div>
        </div>
        <div className="p-2 rounded-lg border border-command-border bg-command-panel">
          <div className="text-[10px] text-gray-400 mb-0.5">Active SOS</div>
          <div className="text-xl font-bold text-red-400">{totals.sos}</div>
        </div>
        <div className="p-2 rounded-lg border border-command-border bg-command-panel">
          <div className="text-[10px] text-gray-400 mb-0.5">Active Alerts</div>
          <div className="text-xl font-bold text-yellow-400">{totals.alerts}</div>
        </div>
      </div>

      {/* Comparison Chart */}
      <div className="p-2 rounded-lg border border-command-border bg-command-panel">
        <h3 className="text-[10px] font-bold text-gray-200 mb-1 uppercase tracking-wide">
          Live Activity by Unit
        </h3>
        <div style={{ height: '320px' }}>
          <Bar data={comparisonData} options={chartOptions} />
        </div>
      </div>

      {/* Detailed Table */}
      <div className="p-2 rounded-lg border border-command-border bg-command-panel">
        <h3 className="text-xs font-bold text-gray-200 mb-2 uppercase tracking-wide">
          📋 Unit Comparison
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-1 px-2 text-gray-400 font-semibold text-[10px]">Unit</th>
                <th className="text-center py-1 px-2 text-gray-400 font-semibold text-[10px]">Active SOS</th>
                <th className="text-center py-1 px-2 text-gray-400 font-semibold text-[10px]">Active Alerts</th>
              </tr>
            </thead>
            <tbody>
              {analysisUnits.map((unit) => {
                const stats = unitStats[unit.code] || { sos: 0, alerts: 0 };
                return (
                  <tr key={unit.id} className="border-b border-gray-800 hover:bg-gray-800/30">
                    <td className="py-1 px-2">
                      <div className="flex items-center gap-1.5">
                        <div
                          className="w-3 h-3 rounded"
                          style={{ backgroundColor: unit.aorColor || '#3b82f6' }}
                        />
                        <span className="font-medium text-gray-200 text-[10px]">{unit.code}</span>
                      </div>
                    </td>
                    <td className="text-center py-1 px-2">
                      <span className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-semibold text-[10px]">
                        {stats.sos}
                      </span>
                    </td>
                    <td className="text-center py-1 px-2">
                      <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 font-semibold text-[10px]">
                        {stats.alerts}
                      </span>
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t-2 border-gray-600 bg-gray-800/30 font-bold">
                <td className="py-1 px-2 text-gray-200 text-[10px]">TOTAL</td>
                <td className="text-center py-1 px-2 text-red-400 text-[10px]">{totals.sos}</td>
                <td className="text-center py-1 px-2 text-yellow-400 text-[10px]">{totals.alerts}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
